import React, { useCallback, useEffect, useState } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getSelections } from 'src/shared/services/selectionService';
import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';

type SelectionDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  setSelectionDetailModal: (state: { open: boolean; selectionId: number }) => void;
  refetchSelectionsDataGrid: boolean;
  setRefetchSelectionsDataGrid: (refetchSelectionsDataGrid: boolean) => void;
  searchString: string;
};

export const SelectionDataGrid = (props: SelectionDataGridProps) => {
  const { loadingCallback, setSelectionDetailModal, refetchSelectionsDataGrid, setRefetchSelectionsDataGrid, searchString } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [rows, setRows] = useState<Selection[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'selectionName', sort: 'asc' }]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }
    try {
      const result = await getSelections(false, data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchString);
      const selectionResult = extendObjectWithId(result.data.results, 'selection_id');

      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(selectionResult);
        }, 100);
        setRefetchSelectionsDataGrid(false);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        loadingCallback(false);
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, isMounted, loadingCallback, searchString, setRefetchSelectionsDataGrid, sortModel]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (refetchSelectionsDataGrid) {
      fetchData();
    }
  }, [refetchSelectionsDataGrid, fetchData]);

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };
  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const onRowClick = (params) => {
    setSelectionDetailModal({ open: true, selectionId: params.row.selection_id });
  };

  const columns: GridColDef[] = [
    {
      field: 'selectionName',
      headerName: t('common:name'),
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.selectionName || ''}</>;
      },
    },
    {
      field: 'selectionInfo',
      headerName: t('member:selection.description'),
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.selectionInfo || ''}</>;
      },
    },
    {
      field: 'created',
      headerName: t('member:selection.created'),
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.created ? DateHelper.getDateTime(params.row.created) : ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<Selection>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
    </Box>
  );
};
